import { joiResolver } from "@hookform/resolvers/joi";
import { RhfInput } from "components/rhfs/RhfInput";
import { Button } from "components/ui/Button";
import { Loading } from "components/ui/Loading/Loading";
import type {
  ConfirmSignUpInput,
  ResendSignUpHandler,
} from "hooks/authProvider/authProvider";
import Joi from "joi";
import type React from "react";
import { FormProvider, type SubmitHandler, useForm } from "react-hook-form";

type SignUpConfirmUIProps = {
  onConfirmSignUp: SubmitHandler<Omit<ConfirmSignUpInput, "email">>;
  onResendSignUp: ResendSignUpHandler;
  isLoading?: boolean;
};

export const SignUpConfirmUI: React.VFC<SignUpConfirmUIProps> = ({
  onConfirmSignUp,
  onResendSignUp,
  isLoading = false,
}) => {
  const schema = Joi.object({
    authCode: Joi.number().integer().required().messages({
      "number.base": "6桁の認証コードを入力してください",
      "number.unsafe": "適切な数値を入力してください",
    }),
  });
  const methods = useForm<Omit<ConfirmSignUpInput, "email">>({
    resolver: joiResolver(schema),
  });

  // ローディング中はスピナーを表示
  if (isLoading) {
    return (
      <div className="flex flex-col items-center py-16">
        <Loading size="md" />
        <p className="text-base-content/60 mt-4">認証中...</p>
      </div>
    );
  }

  return (
    <div className="card bg-base-100 shadow-xl">
      <div className="card-body">
        {/* アイコン */}
        <div className="flex justify-center mb-2">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="w-8 h-8 text-primary"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75"
              />
            </svg>
          </div>
        </div>

        {/* 説明文 */}
        <div className="text-center mb-6">
          <h2 className="text-lg font-semibold mb-2">
            メールを確認してください
          </h2>
          <p className="text-sm text-base-content/60">
            登録されたメールアドレスに認証コードを送信しました。
            <br />
            6桁のコードを入力してください。
          </p>
        </div>

        <FormProvider {...methods}>
          <form
            onSubmit={methods.handleSubmit(onConfirmSignUp)}
            className="space-y-6"
          >
            {/* 認証コード入力 */}
            <RhfInput
              type="text"
              inputMode="numeric"
              name="authCode"
              placeholder="000000"
              maxLength={6}
            />

            {/* 送信ボタン */}
            <Button type="submit" width="full">
              認証する
            </Button>
          </form>
        </FormProvider>

        {/* 再送リンク */}
        <div className="divider text-base-content/50 text-sm my-4">
          コードが届かない場合
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          width="full"
          onClick={e => {
            e.preventDefault();
            onResendSignUp();
          }}
        >
          認証コードを再送する
        </Button>
      </div>
    </div>
  );
};
